/* ============================================================
   Saved work

   Closes a loop that was otherwise broken: results were being written
   to IndexedDB and never shown again. Persisting something a user can
   never retrieve is worse than not persisting it — it is a promise the
   product quietly fails to keep.

   Also the only honest place to offer deletion. A product whose whole
   claim is "your data stays here" must let you see exactly what is
   here, export it, and destroy it.
   ============================================================ */

import { idb } from '../../shared/storage.js';
import {
  readRecords,
  buildExport,
  inspectImport,
  applyImport,
  isKnownKind
} from '../../shared/records.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, percent, longDate } from '../../shared/format.js';
import { toast } from '../../shared/toast.js';
import { router } from '../../shared/router.js';
import { profileSummary, clearProfile, describeAge } from '../../shared/profile.js';
import { prepareLogo, saveLogo, getLogo, clearLogo, ACCEPTED_TYPES } from '../../shared/logo.js';

export function SavedPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Saved work</h1>
      <p>
        Everything this app has stored on your device. Nothing here has been transmitted
        anywhere — you can export it, or destroy it, at any time.
      </p>
    </header>
    <div id="saved-body">
      <div class="empty-state"><div class="spinner" style="margin: 0 auto;"></div></div>
    </div>
  `;

  const body = page.querySelector('#saved-body');
  const paint = () => load(body, paint);
  paint();

  return page;
}

async function load(container, repaint) {
  let scorecards = [];
  let calculations = [];
  let unavailable = false;

  const [s1, c1] = await Promise.all([readRecords('scorecards'), readRecords('benchmarks')]);
  if (s1 === null || c1 === null) unavailable = true;
  scorecards = s1 || [];
  calculations = c1 || [];

  container.replaceChildren();

  if (unavailable) {
    container.innerHTML = html`
      <div class="notice notice-warning">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div style="font-size: var(--ca-text-sm);">
          <strong style="font-weight: var(--ca-weight-medium);">Local storage is unavailable</strong><br />
          This usually means private browsing, or storage disabled for this site. The tools all
          still work — results simply will not be kept between visits.
        </div>
      </div>
    `;
    return;
  }

  const byDate = (a, b) => new Date(b.date) - new Date(a.date);
  scorecards.sort(byDate);
  calculations.sort(byDate);

  if (scorecards.length === 0 && calculations.length === 0) {
    container.innerHTML = html`
      <div class="empty-state">
        <h3>Nothing saved yet</h3>
        <p>
          Complete a scorecard or run a calculation and it will be kept here, on this device,
          for as long as you want it.
        </p>
      </div>
    `;
    const cta = document.createElement('button');
    cta.className = 'btn btn-secondary';
    cta.type = 'button';
    cta.textContent = 'Start a scorecard';
    cta.addEventListener('click', () => router.navigate('/scorecard'));
    container.querySelector('.empty-state').appendChild(cta);

    // Delete the last record one card at a time and the profile survives
    // — still prefilling every form, and, without this, shown nowhere.
    // An invisible store that shapes what the user sees is the thing
    // this page exists to prevent.
    const orphan = profileSummary();
    if (orphan) container.appendChild(profileCard(orphan, repaint));

    // Restore has to be reachable from here. It was previously below the
    // early return, so the one control that puts work back vanished at
    // exactly the moment someone would need it — after erasing, or on a
    // new device holding nothing but a backup file.
    /* Offered with nothing saved, for the same reason Restore is: an
       operator sets their mark before producing the first report, not
       after. Gating it on having work would hide it at exactly the
       moment it is wanted. */
    container.appendChild(logoCard(repaint));
    container.appendChild(dataControls([], [], repaint));
    return;
  }

  if (scorecards.length > 0) {
    const section = document.createElement('section');
    section.className = 'section';
    section.innerHTML = html`<h2 class="section-title">Scorecards (${scorecards.length})</h2>`;
    for (const record of scorecards) {
      section.appendChild(scorecardCard(record, repaint));
    }
    container.appendChild(section);
  }

  if (calculations.length > 0) {
    const section = document.createElement('section');
    section.className = 'section';
    section.innerHTML = html`<h2 class="section-title">Calculations (${calculations.length})</h2>`;
    for (const record of calculations) {
      section.appendChild(calculationCard(record, repaint));
    }
    container.appendChild(section);
  }

  const profile = profileSummary();
  if (profile) container.appendChild(profileCard(profile, repaint));

  container.appendChild(logoCard(repaint));
  container.appendChild(dataControls(scorecards, calculations, repaint));
}

function ragColor(index) {
  if (index < 45) return 'var(--ca-danger)';
  if (index < 65) return 'var(--ca-warning)';
  return 'var(--ca-positive)';
}

function scorecardCard(record, repaint) {
  const color = ragColor(record.healthIndex ?? 0);

  const card = document.createElement('div');
  card.className = 'card';
  card.style.marginBottom = 'var(--ca-space-3)';
  card.innerHTML = html`
    <div class="row" style="align-items: flex-start;">
      <div
        style="width:44px;height:44px;border-radius:var(--ca-radius-md);display:grid;place-items:center;
               background: color-mix(in srgb, ${color} 12%, transparent); color:${color};
               font-weight: var(--ca-weight-semi); flex-shrink:0;"
        class="num"
      >
        ${record.healthIndex ?? '—'}
      </div>
      <div style="flex:1; min-width:0;">
        <div style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
          Corridor Health Scorecard
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          ${longDate(record.date)}${record.confidence !== undefined
            ? html` · ${percent(record.confidence)} confidence`
            : ''}
        </div>
      </div>
      <button class="btn btn-ghost btn-sm" data-delete type="button" aria-label="Delete this scorecard">
        ${raw(icon('close', 15))}
      </button>
    </div>
  `;

  card.querySelector('[data-delete]').addEventListener('click', async () => {
    /* Report what happened, not what was attempted. This used to await a
       rejecting delete bare: in private mode or with the database locked
       the click was inert, the card stayed on screen, and nothing said
       why. Telling someone their work is deleted when it is not is the
       one failure this page cannot afford. */
    if (await idb.deleteQuiet('scorecards', record.id)) {
      toast('Scorecard deleted');
      repaint();
    } else {
      toast('Could not delete it — storage is unavailable on this device.', { tone: 'error' });
    }
  });

  return card;
}

/* ============================================================
   One entry per kind of record that lands in this store.

   Five tools write here and the previous shape of this code knew about
   three of them. Emissions and financeability fell through to a
   fallback that both mislabelled them "Cost-per-Ton-Km Analysis" and
   read `record.costPerTonKm ?? 0` — a field they have never carried —
   so a saved chain assessment displayed "$0.000 per tonne-km". A
   fabricated figure, in the one product that exists to not do that.

   The registry is keyed explicitly, including the corridor calculator,
   which used to be identified by the *absence* of a `kind`. A tool
   added tomorrow that forgets the field now reads as an unrecognised
   record rather than quietly borrowing another tool's identity.
   ============================================================ */
const RECORD_KINDS = {
  benchmark: {
    label: 'Cost-per-Ton-Km Analysis',
    icon: 'coins',
    detail: (r) =>
      [
        Number.isFinite(r.costPerTonKm) ? `$${r.costPerTonKm.toFixed(3)} per tonne-km` : null,
        r.corridor
      ]
        .filter(Boolean)
        .join(' · ')
  },
  'landed-cost': {
    label: 'Landed Cost Assessment',
    icon: 'coins',
    detail: (r) =>
      [`${money(r.landedTotal)} landed`, r.demurrage > 0 ? `${money(r.demurrage)} demurrage` : null]
        .filter(Boolean)
        .join(' · ')
  },
  profitability: {
    label: 'Route Profitability Estimate',
    icon: 'chart',
    detail: (r) => `${money(r.monthlyProfit)} per month · ${percent(r.marginPct, 1)} margin`
  },
  capital: {
    label: 'Capital Stack',
    icon: 'coins',
    /* The worst serviced year, because that is the headline the tool
       gives and a saved record that quietly showed year one would
       contradict the page it came from. */
    detail: (r) =>
      [
        Number.isFinite(r.sources) ? `${money(r.sources)} raised` : null,
        Number.isFinite(r.gearing) ? `${percent(r.gearing, 0)} debt` : null,
        Number.isFinite(r.worstDscr) && Number.isFinite(r.worstYear)
          ? `${number(r.worstDscr, 2)} DSCR in year ${r.worstYear}`
          : null
      ]
        .filter(Boolean)
        .join(' · ')
  },
  emissions: {
    label: 'Corridor Emissions Assessment',
    icon: 'leaf',
    detail: (r) =>
      [
        Number.isFinite(r.intensity) ? `${number(r.intensity, 1)} gCO₂e per tonne-km` : null,
        Number.isFinite(r.totalKg) ? `${number(r.totalKg)} kgCO₂e total` : null,
        r.quality ? `${r.quality} data` : null
      ]
        .filter(Boolean)
        .join(' · ')
  },
  'corridor-run': {
    label: 'Corridor Run Assessment',
    icon: 'truck',
    detail: (r) =>
      [
        Number.isFinite(r.costPerTonKm) ? `$${r.costPerTonKm.toFixed(3)} per tonne-km` : null,
        Number.isFinite(r.profit) ? `${money(r.profit)} margin` : null,
        r.demurrage > 0 ? `${money(r.demurrage)} demurrage` : null,
        r.findings > 0 ? `${r.findings} finding${r.findings === 1 ? '' : 's'}` : null
      ]
        .filter(Boolean)
        .join(' · ')
  },
  financeability: {
    label: 'Financeability Pack',
    icon: 'shield',
    detail: (r) =>
      [
        Number.isFinite(r.score) ? `Score ${r.score}/100` : null,
        Number.isFinite(r.dscr) ? `DSCR ${number(r.dscr, 2)}` : 'DSCR n/a',
        Number.isFinite(r.ccc) ? `${number(r.ccc)}-day cash cycle` : null
      ]
        .filter(Boolean)
        .join(' · ')
  }
};

/* Records written before the corridor calculator carried a `kind`. */
const UNKINDED_IS_BENCHMARK = (r) => !r.kind && Number.isFinite(r.costPerTonKm);

const UNKNOWN_KIND = {
  label: 'Saved calculation',
  icon: 'document',
  // Deliberately says nothing. Inventing a zero is how this broke before.
  detail: () => ''
};

function calculationCard(record, repaint) {
  // Own-property lookup. A record can now arrive from an imported file,
  // so `kind` is untrusted input: plain indexing would let
  // {"kind":"constructor"} return a truthy function and walk past the
  // fallback. Same class as audit A-02 in the brand switcher.
  const spec =
    (isKnownKind(record.kind) && Object.prototype.hasOwnProperty.call(RECORD_KINDS, record.kind)
      ? RECORD_KINDS[record.kind]
      : null) || (UNKINDED_IS_BENCHMARK(record) ? RECORD_KINDS.benchmark : UNKNOWN_KIND);
  const label = spec.label;

  let detail = '';
  try {
    detail = spec.detail(record) || '';
  } catch {
    // A malformed record must not take the whole Saved page down with it.
    detail = '';
  }

  const card = document.createElement('div');
  card.className = 'card';
  card.style.marginBottom = 'var(--ca-space-3)';
  card.innerHTML = html`
    <div class="row" style="align-items: flex-start;">
      <div
        style="width:44px;height:44px;border-radius:var(--ca-radius-md);display:grid;place-items:center;
               background: var(--ca-surface-sunk); color: var(--ca-text-tertiary); flex-shrink:0;"
      >
        ${raw(icon(spec.icon, 20))}
      </div>
      <div style="flex:1; min-width:0;">
        <div style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">${label}</div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">${detail}</div>
        <div style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary);">
          ${longDate(record.date)}
        </div>
      </div>
      <button class="btn btn-ghost btn-sm" data-delete type="button" aria-label="Delete this calculation">
        ${raw(icon('close', 15))}
      </button>
    </div>
  `;

  card.querySelector('[data-delete]').addEventListener('click', async () => {
    if (await idb.deleteQuiet('benchmarks', record.id)) {
      toast('Calculation deleted');
      repaint();
    } else {
      toast('Could not delete it — storage is unavailable on this device.', { tone: 'error' });
    }
  });

  return card;
}

/* The profile prefills forms across the suite, so it has to be visible
   and destroyable here like everything else. A store that quietly shapes
   what the user sees, and that they cannot inspect, would break the one
   promise this product makes. */
function profileCard(profile, repaint) {
  const card = document.createElement('section');
  card.className = 'section';
  card.innerHTML = html`
    <h2 class="section-title">Remembered about your operation (${profile.count})</h2>
    <div class="card">
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-3);">
        Used to fill in blank fields across the tools so you do not enter the same figure four
        times. Last updated ${describeAge(profile.ageDays)}. Nothing here is transmitted, and
        anything older than 90 days is dropped rather than offered as current.
      </p>
      <div class="row" style="flex-wrap: wrap; gap: var(--ca-space-2); margin-bottom: var(--ca-space-4);">
        ${profile.fields.map(
          (f) => html`
            <span class="badge" style="background: var(--ca-surface-sunk); color: var(--ca-text-secondary);">
              ${f.label}: <span class="num">${String(f.value)}</span>${f.unit}
            </span>
          `
        )}
      </div>
      <button class="btn btn-secondary btn-sm" id="forget-profile" type="button">Forget this</button>
    </div>
  `;

  card.querySelector('#forget-profile').addEventListener('click', () => {
    clearProfile();
    toast('Operation details forgotten');
    repaint();
  });

  return card;
}

/* ---- the operator's mark on the operator's reports ----

   Lives on this page rather than in a settings screen because this is
   the page about what is stored on the device, and a logo is stored on
   the device. It is exported with everything else, erased with
   everything else, and never leaves — the same promise as the rest.

   The preview is rendered at the real print size, not at a convenient
   one. A logo that looks fine at 200px and turns to mush inside a 14mm
   header box is the failure this feature invites, and the only honest
   way to show it is to show it at 14mm. */
function logoCard(repaint) {
  const stored = getLogo();

  const card = document.createElement('section');
  card.className = 'card card-tinted';
  card.style.marginTop = 'var(--ca-space-6)';

  card.innerHTML = html`
    <div class="row" style="margin-bottom: var(--ca-space-2);">
      <span style="color: var(--ca-text-tertiary);">${raw(icon('document', 16))}</span>
      <span style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
        Your logo on exports
      </span>
    </div>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-4);">
      ${stored
        ? html`Every report you print or save as PDF carries this mark above the title.
            It is stored on this device with the rest of your work and is sent nowhere.`
        : html`Add your logo and every report you print or save as PDF will carry it above
            the title, in place of the name. PNG, JPEG or WebP; it is resized here on your
            device and sent nowhere.`}
    </p>

    ${stored
      ? html`
        <div class="row" style="gap: var(--ca-space-4); align-items: center; flex-wrap: wrap;
                    margin-bottom: var(--ca-space-4);">
          <div style="padding: var(--ca-space-2); background: #fff; border: 1px solid var(--ca-border);
                      border-radius: var(--ca-radius-sm);">
            <img src="${stored.dataUrl}" alt="Your logo, shown at the size it prints"
                 style="max-height: 14mm; max-width: 60mm; object-fit: contain; display: block;" />
          </div>
          <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
            Shown at print size — 14mm tall, 60mm wide.<br />
            Stored copy ${stored.width}×${stored.height}px.
          </div>
        </div>`
      : ''}

    <div class="row" style="gap: var(--ca-space-2); flex-wrap: wrap;">
      <button class="btn btn-secondary btn-sm" id="logo-pick" type="button">
        ${stored ? 'Replace logo' : 'Add a logo'}
      </button>
      <input id="logo-file" type="file" accept="${ACCEPTED_TYPES.join(',')}"
             aria-label="Choose an image file to use as your logo on exports" hidden />
      ${stored
        ? html`<button class="btn btn-secondary btn-sm" id="logo-clear" type="button">
            Remove
          </button>`
        : ''}
    </div>
    <p id="logo-error" role="alert" aria-live="polite"
       style="font-size: var(--ca-text-xs); color: var(--ca-danger); margin: var(--ca-space-3) 0 0;"></p>
  `;

  const input = card.querySelector('#logo-file');
  const problem = card.querySelector('#logo-error');

  card.querySelector('#logo-pick')?.addEventListener('click', () => input.click());

  input?.addEventListener('change', async () => {
    problem.textContent = '';
    const file = input.files?.[0];
    /* Reset before the await, not after. Choosing the same file twice
       in a row fires no change event if the value is still set, and the
       operator who fixed their PNG and re-picked it would see nothing
       happen. */
    input.value = '';
    if (!file) return;

    const prepared = await prepareLogo(file);
    if (!prepared.ok) {
      problem.textContent = prepared.reason;
      return;
    }
    /* saveLogo returns false when storage refused the write, and that
       path has already told the user why through the shared notice —
       so repaint only on a write that actually happened, or the card
       would redraw claiming a logo that is not there. */
    if (saveLogo(prepared)) {
      toast('Logo saved — it will appear on your exports');
      repaint();
    }
  });

  card.querySelector('#logo-clear')?.addEventListener('click', () => {
    clearLogo();
    toast('Logo removed');
    repaint();
  });

  return card;
}

function dataControls(scorecards, calculations, repaint) {
  const hasWork = scorecards.length > 0 || calculations.length > 0;

  const wrap = document.createElement('section');
  wrap.className = 'card card-tinted';
  wrap.style.marginTop = 'var(--ca-space-6)';

  wrap.innerHTML = html`
    <div class="row" style="margin-bottom: var(--ca-space-2);">
      <span style="color: var(--ca-text-tertiary);">${raw(icon('lock', 16))}</span>
      <span style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">Your data</span>
    </div>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-4);">
      ${hasWork
        ? html`Export writes a JSON file to your device, and Restore reads one back — the export
            would otherwise be a one-way door. Restoring merges into what is already here rather
            than replacing it. Erase removes everything permanently; there is no copy anywhere
            else, so it cannot be recovered.`
        : html`Nothing is stored here at the moment. If you have an export from this or another
            device, restore it — that file is the only copy, because nothing was ever sent
            anywhere.`}
    </p>
    <div class="row" style="gap: var(--ca-space-2); flex-wrap: wrap;">
      ${hasWork
        ? html`<button class="btn btn-secondary btn-sm" id="export-data" type="button">
            Export as JSON
          </button>`
        : ''}
      <button class="btn btn-secondary btn-sm" id="import-data" type="button">Restore from a file</button>
      <input id="import-file" type="file" accept="application/json,.json"
             aria-label="Choose a Kanda export file to restore" hidden />
      ${hasWork
        ? html`<button class="btn btn-secondary btn-sm" id="erase-data" type="button"
                 style="color: var(--ca-danger); border-color: color-mix(in srgb, var(--ca-danger) 30%, var(--ca-border));">
            Erase everything
          </button>`
        : ''}
    </div>
  `;

  wrap.querySelector('#export-data')?.addEventListener('click', () => {
    const payload = buildExport({
      scorecards,
      calculations,
      profile: profileSummary()
    });
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kanda-export-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast('Export downloaded');
  });

  /* ---- Restore ----
     The file is treated as hostile: every record is validated against
     the known kinds before anything is written, and whatever is refused
     is reported rather than dropped in silence. A restore that quietly
     discarded half a backup would be worse than one that failed. */
  const fileInput = wrap.querySelector('#import-file');
  wrap.querySelector('#import-data').addEventListener('click', () => fileInput.click());

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    fileInput.value = ''; // so the same file can be chosen twice

    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast('That file is not valid JSON');
      return;
    }

    const inspected = inspectImport(parsed);
    if (!inspected.ok) {
      toast(inspected.error);
      return;
    }

    const { written, failed, rejected } = await applyImport(inspected);

    const parts = [`${written} restored`];
    if (rejected > 0) parts.push(`${rejected} not recognised`);
    if (failed > 0) parts.push(`${failed} could not be written`);
    toast(parts.join(' · '));
    repaint();
  });

  const eraseBtn = wrap.querySelector('#erase-data');
  let armed = false;
  eraseBtn?.addEventListener('click', async () => {
    if (!armed) {
      armed = true;
      eraseBtn.textContent = 'Tap again to confirm';
      setTimeout(() => {
        armed = false;
        eraseBtn.textContent = 'Erase everything';
      }, 4000);
      return;
    }
    /* Both stores, and the answer to each. The old form swallowed any
       rejection and announced success regardless — on a device where
       IndexedDB is blocked, someone who armed a destructive action and
       confirmed it was told their work was gone while it sat there. */
    const cleared = await Promise.all([
      idb.clear('scorecards').then(() => true, () => false),
      idb.clear('benchmarks').then(() => true, () => false)
    ]);
    // "Everything" has to mean everything, including the profile that
    // would otherwise repopulate the next form the user opened. This is
    // localStorage rather than IndexedDB and is already guarded, so it
    // runs whether or not the stores cleared.
    clearProfile();
    /* And the logo, for the reason directly above. It is the operator's
       own mark, it is on this device, and a button reading "Erase
       everything" on the page whose whole promise is that you can
       destroy what is here must not leave it sitting in localStorage.
       Same reasoning as the profile, same guarded storage. */
    clearLogo();
    repaint();

    if (cleared.every(Boolean)) {
      toast('All saved work erased');
    } else if (cleared.some(Boolean)) {
      toast('Some saved work could not be erased — what remains is still listed.', { tone: 'error' });
    } else {
      toast('Nothing could be erased — storage is unavailable on this device.', { tone: 'error' });
    }
  });

  return wrap;
}
